import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Check, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { VET_PLANS } from '../mock';
import api from '../api';

export default function VetSignup() {
  const nav = useNavigate();
  const [plan, setPlan] = useState('premium');
  const [form, setForm] = useState({ clinic: '', email: '', phone: '', address: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.clinic || !form.email) { toast.error('Please fill in clinic name and email'); return; }
    setBusy(true);
    try {
      await api.post('/vet/signup', { ...form, plan });
      const lookup = plan === 'premium' ? 'purrpal_vet_premium' : 'purrpal_vet_basic';
      const r = await api.post('/payments/checkout', { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start checkout'); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      <div className="mx-auto max-w-2xl px-5 py-6">
        <button onClick={() => nav('/vets')} className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="text-3xl font-extrabold tracking-tight animate-fade-up">Create your vet account.</h1>
        <p className="mt-2 text-muted-foreground">List your clinic and start receiving cat-parent leads.</p>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-sm font-semibold">Clinic name</Label>
            <Input value={form.clinic} onChange={set('clinic')} placeholder="Whiskers & Co. Cat Clinic" className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Email</Label>
            <Input type="email" value={form.email} onChange={set('email')} placeholder="clinic@example.com" className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold">Phone</Label>
              <Input value={form.phone} onChange={set('phone')} placeholder="+1 555 0100" className="mt-1.5 h-12 rounded-xl bg-white" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Address</Label>
              <Input value={form.address} onChange={set('address')} placeholder="210 Maple Ave" className="mt-1.5 h-12 rounded-xl bg-white" />
            </div>
          </div>
        </div>

        <h2 className="mt-8 text-lg font-extrabold">Choose your plan</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {VET_PLANS.map((p) => (
            <button key={p.id} onClick={() => setPlan(p.id)} className={`relative rounded-2xl border-2 bg-white p-5 text-left transition ${plan === p.id ? 'border-[hsl(var(--coral))] bg-accent/40' : 'border-border'}`}>
              {p.best && <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--coral))] px-2 py-0.5 text-[10px] font-bold uppercase text-white"><Star className="h-3 w-3" />Best</span>}
              <p className="font-bold">{p.name}</p>
              <p className="mt-1"><span className="text-3xl font-extrabold text-[hsl(var(--coral))]">${p.price}</span><span className="text-sm text-muted-foreground">/mo</span></p>
              {plan === p.id && <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[hsl(var(--coral))]"><Check className="h-4 w-4" />Selected</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 border-t border-border bg-background/95 px-5 py-4 backdrop-blur">
        <div className="mx-auto max-w-2xl">
          <button onClick={submit} disabled={busy} className="coral-btn flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">{busy && <Loader2 className="h-5 w-5 animate-spin" />}Continue to checkout</button>
        </div>
      </div>
    </div>
  );
}
